/* eslint-disable @typescript-eslint/no-explicit-any */
import Web3 from 'web3';

// Extend the Window interface to include Ethereum
declare global {
  interface Window {
    ethereum: {
      request: (args: { method: string }) => Promise<string>;
      send: (method: string) => Promise<void>;
      enable: () => Promise<string[]>;
      selectedAddress: string | null;
      networkVersion: string;
    };
    web3?: {
      currentProvider: any;
    };
  }
}

let web3: Web3 | null = null;
let isInitializing = false;

// Force switch to BSC Testnet (Network ID 97)
const switchToBSC = async (): Promise<boolean> => {
  if (!window.ethereum) {
    return false;
  }

  try {
    // Try to switch to BSC Testnet
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: '0x61' }], // BSC Testnet chain ID
    } as any);
    return true;
  } catch (error: any) {
    // If the network doesn't exist, try to add it
    if (error.code === 4902) {
      try {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: '0x61',
              chainName: 'BSC Testnet',
              rpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545/'],
              blockExplorerUrls: ['https://testnet.bscscan.com'],
            },
          ],
        } as any);
        return true;
      } catch (addError) {
        return false;
      }
    }
    return false;
  }
};

const getWeb3 = async (): Promise<Web3 | null> => {
  // Prevent multiple simultaneous initializations
  if (isInitializing) {
    // Wait for initialization to complete with timeout
    const maxWaitTime = 30000; // 30 seconds
    const startTime = Date.now();
    while (isInitializing && Date.now() - startTime < maxWaitTime) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (isInitializing) {
      isInitializing = false;
    }
    return web3;
  }

  if (web3) {
    // Test existing connection
    try {
      await web3.eth.getBlockNumber();
      return web3;
    } catch (error) {
      web3 = null;
    }
  }

  isInitializing = true;

  try {
    // Try MetaMask first
    if (typeof window !== 'undefined' && window.ethereum) {
      try {
        web3 = new Web3(window.ethereum);

        // Test connection with timeout
        const connectionPromise = web3.eth.getBlockNumber();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Connection timeout')), 10000)
        );

        await Promise.race([connectionPromise, timeoutPromise]);

        // Check if already connected
        try {
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts.length === 0) {
            // Request account access if needed
            await window.ethereum.request({ method: 'eth_requestAccounts' });
          }
        } catch (accountError) {
          console.error('Account access denied:', accountError);
        }
      } catch (metamaskError) {
        web3 = null;
      }
    }

    // Try legacy web3 provider
    if (!web3 && typeof window !== 'undefined' && window.web3) {
      try {
        web3 = new Web3(window.web3.currentProvider);
        await web3.eth.getBlockNumber();
      } catch (legacyError) {
        web3 = null;
      }
    }

    // Fallback to HTTP providers
    if (!web3) {
      // BSC Testnet RPC endpoints with better reliability
      const rpcEndpoints = [
        'https://data-seed-prebsc-1-s1.binance.org:8545/',
        'https://data-seed-prebsc-2-s1.binance.org:8545/',
        'https://data-seed-prebsc-1-s2.binance.org:8545/',
        'https://data-seed-prebsc-2-s2.binance.org:8545/',
        'https://data-seed-prebsc-1-s3.binance.org:8545/',
        'https://data-seed-prebsc-2-s3.binance.org:8545/',
      ];

      for (const endpoint of rpcEndpoints) {
        try {
          const provider = new Web3.providers.HttpProvider(endpoint);

          web3 = new Web3(provider);

          // Test connection with timeout
          const testPromise = web3.eth.getBlockNumber();
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('RPC timeout')), 8000)
          );

          await Promise.race([testPromise, timeoutPromise]);
          break;
        } catch (error) {
          web3 = null;
        }
      }
    }

    // Final connection test
    if (web3) {
      try {
        await web3.eth.getBlockNumber();

        // Check network and switch to BSC Testnet if needed
        try {
          const networkId = await web3.eth.net.getId();

          if (Number(networkId) !== 97) {
            // Don't block initialization for network switching
            setTimeout(async () => {
              try {
                await switchToBSC();
              } catch (switchError) {
                console.error('Network switch failed:', switchError);
              }
            }, 2000);
          } else {
            console.log('Already on BSC network');
          }
        } catch (networkError) {
          console.error('Network detection failed:', networkError);
        }
      } catch (connectionError) {
        web3 = null;
      }
    }

    if (!web3) {
      console.error('Web3 initialization failed');
    }
  } catch (error) {
    web3 = null;
  } finally {
    isInitializing = false;
  }

  return web3;
};

/**
 * Disconnect wallet: clear in-memory web3 instance so next login will prompt MetaMask again.
 * Call this on admin logout.
 */
function disconnectWallet(): void {
  web3 = null;
  isInitializing = false;
}

// Do not auto-call getWeb3 on load; wallet is connected after admin sign-in (see login flow).
// getWeb3() is called explicitly when needed (e.g. after login, or by features that use the wallet).

// Make network switching available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).switchToBSC = switchToBSC;
}

export { getWeb3, switchToBSC, web3, disconnectWallet };
